import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Calendar, Clock, ChevronLeft, ChevronRight, Loader2, Check, User, Mail
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [bookingInfo, setBookingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState("date"); // date -> time -> details -> confirmed
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    fetchBookingInfo();
  }, [slug]);

  const fetchBookingInfo = async () => {
    try {
      const res = await fetch(`${API}/book/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setBookingInfo(data);
      } else {
        setError("Booking page not found");
      }
    } catch (e) {
      setError("Failed to load booking page");
    } finally {
      setLoading(false);
    }
  };

  const selectDate = async (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const res = await fetch(`${API}/book/${slug}/slots?date=${dateStr}`);
      if (res.ok) {
        const data = await res.json();
        setSlots(data.slots || []);
      }
    } catch (e) {
      toast.error("Failed to load time slots");
    } finally {
      setLoadingSlots(false);
      setStep("time");
    }
  };

  const handleBook = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBooking(true);
    try {
      const res = await fetch(`${API}/book/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email,
          phone: form.phone, notes: form.notes,
          start_time: selectedSlot.datetime
        })
      });
      if (res.ok) {
        setStep("confirmed");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Booking failed");
      }
    } catch (e) {
      toast.error("Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const navigateMonth = (dir) => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + dir);
    setCurrentMonth(d);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700">Page Not Found</h2>
            <p className="text-slate-500 mt-2">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calendar generation
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

  const isDateAvailable = (day) => {
    if (!day) return false;
    const d = new Date(year, month, day);
    if (d < today) return false;
    const dayName = d.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const avail = bookingInfo?.availability?.[dayName];
    return avail?.enabled || false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4" data-testid="public-booking-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Calendar className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="booking-host-name">
            Book a meeting with {bookingInfo?.user_name}
          </h1>
          <p className="text-slate-500 mt-1">
            {bookingInfo?.default_duration} minute meeting
          </p>
        </div>

        {/* Confirmed */}
        {step === "confirmed" && (
          <Card data-testid="booking-confirmed">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Booking Confirmed!</h2>
              <p className="text-slate-500 mt-2">
                Your appointment on <strong>{selectedDate}</strong> at <strong>{selectedSlot?.time}</strong> has been booked.
              </p>
              <p className="text-sm text-slate-400 mt-4">A confirmation email has been sent to {form.email}</p>
            </CardContent>
          </Card>
        )}

        {/* Details step */}
        {step === "details" && (
          <Card data-testid="booking-details-form">
            <CardHeader>
              <Button variant="ghost" size="sm" onClick={() => setStep("time")} className="w-fit mb-2">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <CardTitle>Enter your details</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                {selectedDate} at {selectedSlot?.time} - {bookingInfo?.default_duration} min
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="booking-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" data-testid="booking-email" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="booking-phone" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Anything you'd like to discuss..." rows={3} data-testid="booking-notes" />
              </div>
              <Button className="w-full" onClick={handleBook} disabled={booking} data-testid="confirm-booking-btn">
                {booking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Confirm Booking
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Date & Time selection */}
        {(step === "date" || step === "time") && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Calendar */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="font-semibold text-slate-700">{MONTHS[month]} {year}</span>
                    <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {DAYS.map(d => <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">{d}</div>)}
                    {calendarDays.map((day, i) => {
                      const available = isDateAvailable(day);
                      const dateStr = day ? `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}` : "";
                      const isSelected = dateStr === selectedDate;
                      return (
                        <button
                          key={i}
                          disabled={!available}
                          onClick={() => available && selectDate(dateStr)}
                          className={`h-10 rounded-lg text-sm font-medium transition-all
                            ${!day ? "" : ""}
                            ${available && !isSelected ? "hover:bg-primary/10 text-slate-700 cursor-pointer" : ""}
                            ${available && isSelected ? "bg-primary text-white" : ""}
                            ${!available && day ? "text-slate-300 cursor-not-allowed" : ""}
                          `}
                          data-testid={dateStr ? `date-${dateStr}` : undefined}
                        >
                          {day || ""}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time slots */}
                {step === "time" && selectedDate && (
                  <div className="flex-1 border-l border-slate-200 pl-6" data-testid="time-slots">
                    <p className="font-semibold text-slate-700 mb-3">
                      {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                    </p>
                    {loadingSlots ? (
                      <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : slots.length === 0 ? (
                      <p className="text-slate-400 text-sm py-8 text-center">No available slots for this date</p>
                    ) : (
                      <div className="space-y-2 max-h-[340px] overflow-y-auto pr-2">
                        {slots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => { setSelectedSlot(slot); setStep("details"); }}
                            className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm font-medium transition-all
                              ${selectedSlot?.time === slot.time
                                ? "border-primary bg-primary/5 text-primary"
                                : "border-slate-200 hover:border-primary/40 text-slate-700"
                              }`}
                            data-testid={`slot-${slot.time}`}
                          >
                            <Clock className="w-3.5 h-3.5 inline mr-2" />
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Sales CRM Coach
        </p>
      </div>
    </div>
  );
}
